import { useMemo, useState } from "react";
import { DateLib, DayPicker, type DateLibOptions, type Modifiers } from "react-day-picker";
import { sq } from "date-fns/locale";
import "react-day-picker/style.css";
import { Card, CardBody } from "../components/ui/Card";
import { ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { EventPanel } from "../components/EventPanel";
import { calendar as calendarCopy } from "../copy/sq";
import { parseLocalIsoDate, toLocalIsoDate } from "../lib/date";
import { useCalendarRange } from "../hooks/useApi";

/** Padding around the visible month so leading/trailing grid days (from the adjacent month) also get their events fetched — well within the Worker's 120-day bound for a single month view. */
const RANGE_PADDING_DAYS = 7;

// All arithmetic here stays in LOCAL calendar-day terms (matching
// react-day-picker's own `month`/`selected` Date objects and this
// screen's plain-date event fields) — see toLocalIsoDate's docstring for
// why mixing in UTC-based Date math would silently shift days for
// viewers outside UTC.
function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}
function sameIsoDay(a: string, b: Date): boolean {
  return a === toLocalIsoDate(b);
}

// react-day-picker's own ARIA label strings default to English regardless
// of the `locale` prop (locale only drives date-fns formatting of the
// date itself — weekday/month names, which do come through in Albanian
// automatically). These three are the ones actually reachable in this
// screen (no week numbers, no month/year dropdowns), reimplemented from
// react-day-picker's own default labelDayButton (see its source) with
// Albanian wording substituted in.
const dayPickerLabels = {
  labelPrevious: () => calendarCopy.labelPrevious,
  labelNext: () => calendarCopy.labelNext,
  labelDayButton: (date: Date, modifiers: Modifiers, options?: DateLibOptions, dateLib?: DateLib) => {
    let label = (dateLib ?? new DateLib(options)).format(date, "PPPP");
    if (modifiers.today) label = `${calendarCopy.todayPrefix}${label}`;
    if (modifiers.selected) label = `${label}${calendarCopy.selectedSuffix}`;
    return label;
  },
};

export function CalendarScreen() {
  const [month, setMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const rangeStart = useMemo(() => toLocalIsoDate(addDays(startOfMonth(month), -RANGE_PADDING_DAYS)), [month]);
  const rangeEnd = useMemo(() => toLocalIsoDate(addDays(endOfMonth(month), RANGE_PADDING_DAYS)), [month]);

  const { data, isLoading, isError, error, refetch } = useCalendarRange(rangeStart, rangeEnd);

  const eventDates = useMemo(() => {
    if (!data) return [] as Date[];
    const uniqueIso = new Set(data.items.map((event) => event.start_date));
    return Array.from(uniqueIso).map(parseLocalIsoDate);
  }, [data]);

  const selectedDayEvents = useMemo(() => {
    if (!data || !selectedDate) return [];
    return data.items.filter((event) => sameIsoDay(event.start_date, selectedDate));
  }, [data, selectedDate]);

  if (isError) {
    return <ErrorState title={calendarCopy.errorTitle} message={error instanceof Error ? error.message : undefined} onRetry={() => refetch()} />;
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[auto,1fr]">
      <Card className="w-full sm:w-fit">
        <CardBody className="overflow-x-auto">
          {isLoading ? (
            <Skeleton className="h-[320px] w-[280px]" />
          ) : (
            <DayPicker
              mode="single"
              locale={sq}
              labels={dayPickerLabels}
              month={month}
              onMonthChange={setMonth}
              selected={selectedDate ?? undefined}
              onSelect={(date) => setSelectedDate(date ?? null)}
              modifiers={{ hasEvent: eventDates }}
              modifiersClassNames={{ hasEvent: "rdp-day_has-event" }}
              className="rdp-newsroom"
              showOutsideDays
            />
          )}
          <div className="mt-3 flex items-center gap-2 px-1 text-xs text-ink-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500" /> {calendarCopy.legend}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <EventPanel date={selectedDate} events={selectedDayEvents} />
        </CardBody>
      </Card>
    </div>
  );
}
